import React from 'react';
import { Dialog, Flex, IconButton, Tooltip } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { CheckIcon, NotInterestedIcon } from 'shared/icons';
import { TransactionConfirmDialog } from '../transaction-confirm-dialog';
import { useTransactions } from '../transactions.provider';
import { Transaction } from '../types';

import './transaction-actions-menu.scss';

type TransactionAction = 'ACTIVATE' | 'DEACTIVATE' | 'EMPTY';

const TransactionActionsMenuView = ({ transaction }: { transaction: Transaction }) => {
   const transactionsStore = useTransactions();
   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<TransactionAction>('EMPTY');

   return (
      <Flex className="transaction-actions-menu" gap="1" align="center" justify="center">
         {transaction.status ? (
            <Tooltip content="Deactivate" side="left">
               <IconButton
                  color="green"
                  className="action-trigger"
                  disabled={transactionsStore.actionLoading}
                  onClick={() => {
                     setOpen(true);
                     setAction('DEACTIVATE');
                  }}
               >
                  <CheckIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         ) : (
            <Tooltip content="Activate" side="left">
               <IconButton
                  color="red"
                  className="action-trigger"
                  disabled={transactionsStore.actionLoading}
                  onClick={() => {
                     setOpen(true);
                     setAction('ACTIVATE');
                  }}
               >
                  <NotInterestedIcon width={20} height={20} />
               </IconButton>
            </Tooltip>
         )}

         <Dialog.Root
            open={open}
            onOpenChange={(nextOpen) => {
               if (!nextOpen) {
                  setAction('EMPTY');
               }
               setOpen(nextOpen);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(event) => event.preventDefault()}
            >
               {
                  {
                     ACTIVATE: (
                        <TransactionConfirmDialog
                           title="Activate User Plan"
                           message="Do you want to activate this user plan?"
                           confirmLabel="Activate"
                           confirmColor="green"
                           loading={transactionsStore.activateTransactionState.status === 'pending'}
                           onConfirm={() => {
                              transactionsStore.activateTransaction({
                                 id: transaction.id,
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     DEACTIVATE: (
                        <TransactionConfirmDialog
                           title="Deactivate User Plan"
                           message="Do you want to deactivate this user plan?"
                           confirmLabel="Deactivate"
                           confirmColor="red"
                           loading={
                              transactionsStore.deactivateTransactionState.status === 'pending'
                           }
                           onConfirm={() => {
                              transactionsStore.deactivateTransaction({
                                 id: transaction.id,
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success(resp.message);
                                 },
                                 onError: (message) => {
                                    toast.error(message);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </Flex>
   );
};

export const TransactionActionsMenu = observer(TransactionActionsMenuView);
